import { useState, useCallback } from 'react';
import validator from '../../components/FieldValidator.jsx';
import LegalEntityFunctions from '../../components/LegalEntityFunctions.jsx';
import urlTools from '../../tools/urlTools.jsx';
import webTools from '../../tools/webTools.js';
import AuthService from '../../services/AuthService.js';
import ErrorMessagesService from '../../services/ErrorMessagesService.js';
import { pushAccountEvent } from '../../tools/GaEvents/GaEventSender.jsx';
import { GaEventActionConstants } from '../../tools/GaEvents/GaEventActionConstants.jsx';
import { RECAPTCHA_CHALLENGE_MAIL, RECAPTCHA_CHALLENGE_PHONE, RECAPTCHA_ERROR } from '../../constants/errorCodes.jsx';
import * as constants from '../../constants/constants.jsx';
import i18n from 'translations/i18n.jsx';

export function usePasswordLoginLogic(initialData, location, history) {
  const [state, setState] = useState({
    model: { username: "", password: "" },
    errors: {},
    selectedCountry: null,
    clientId: "",
    legalEntity: "",
    showSpinner: true,
    phoneError: false,
    selectedPrefixId: "",
    businessLogin: false,
    maxPhoneLength: "",
    showEmailSent: false,
    emailHint: "",
    email: "",
    birthDate: null,
    emailError: "",
    birthDateError: "",
    showEnterEmail: false,
    isMobileApp: false,
    isProcessing: false,
    isLoginTypeSwitchHidden: initialData.businessLoginDisabled
  });

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const onChange = useCallback((e) => {
    const model = { ...state.model };
    model[e.prop] = e.prop === 'username' ? validator.blockSpaceSymbol(e.value) : e.value;
    updateState({ model, errors: {} });
  }, [state.model, updateState]);

  const switchLogin = useCallback((e) => {   
    e.preventDefault(); 
    const model = { ...state.model, password: "" };
    updateState({
      model,
      errors: {},
      businessLogin: !state.businessLogin
    });
  }, [state.model, state.businessLogin, updateState]);

  const setIsProcessing = useCallback((isProcessing) => {
    updateState({ isProcessing });
  }, [updateState]);

  const executeUnauthenticatedRedirections = useCallback(() => {
    const flags = initialData.unauthenticatedRedirectionFlags;
    switch (true) {
      case flags.redirectToRegistration:
        history.push("/registration/NoAccount/CreateNewMyRimiAccount");
        break;
      case flags.redirectToRemindEmail:
        history.push("/PasswordRecovery/RemindEmail");
        break;
      case flags.redirectToPasswordReset:
        history.push("/PasswordRecovery/EnterEmail");
        break;
      case flags.redirectToCardReplacement:
        history.push("/cardReplacementLogin");
        break;
    }
  }, [initialData, history]);

  const getSelectedCountryPrefixValue = useCallback(() => {
    const selectedCountry = localStorage.getItem(constants.SELECTED_COUNTRY);
    return LegalEntityFunctions.getPhonePrefix(selectedCountry);
  }, []);

  const getSelectedCountryPrefixId = useCallback(() => {
    const selectedCountry = localStorage.getItem(constants.SELECTED_COUNTRY);
    return LegalEntityFunctions.getPhonePrefixId(selectedCountry);
  }, []);

  const getCompleteUsername = useCallback((currentState) => {
    return LegalEntityFunctions.getCompletePhoneNumber(
      currentState.model.username,
      currentState.selectedPrefixId,
      initialData.phonePrefixes
    );
  }, [initialData.phonePrefixes]);

  const processLoginResult = useCallback(async (result, previousError, currentState) => {
    const errors = { ...currentState.errors };
    const model = { ...currentState.model };
    let error = previousError;
    let clearPassword = true;

    if (!result.data.success) {
      const usersLanguage = result.data.resultObject?.usersLanguageCode;
      if (usersLanguage) {
        const languageIsoCode = LegalEntityFunctions.getLanguageIsoCode(usersLanguage);
        i18n.changeLanguage(languageIsoCode);
      }

      error = true;
      pushAccountEvent(GaEventActionConstants.UserLoginFail);

      const errorMapping = ErrorMessagesService.getLoginErrorMapping(result.data.error);
      
      if (errorMapping) {
        errors[errorMapping.field] = errorMapping.message;
        
        if (errorMapping.requiresPhoneSetup && currentState.selectedPrefixId === "") {
          const selectedCountryPrefix = getSelectedCountryPrefixValue();
          const maxlength = validator.getPhoneMinLength(initialData.phonePrefixes, selectedCountryPrefix);
          updateState({ 
            phoneError: true,
            selectedPrefixId: getSelectedCountryPrefixId(), 
            maxPhoneLength: maxlength 
          });
        } else if (errorMapping.requiresPhoneSetup) {
          updateState({ phoneError: true });
        }
      } else if (result.data.error === "noEmail") {
        clearPassword = false;
        updateState({ 
          showEnterEmail: true, 
          clientId: result.data.resultObject.clientId, 
          legalEntity: result.data.resultObject.legalEntity 
        });
      } else if (result.data.error === "emailNotverified") {
        try {
          const resultSendVerification = await AuthService.sendEmailVerification(result.data.resultObject.email);
          const sendVerificationResponse = resultSendVerification.response;

          if (!sendVerificationResponse.data.success) {
            errors['onLogin'] = ["rate_limit_reached"];
            updateState({ error: true, errors });
          } else {
            updateState({ showEmailSent: true, emailHint: result.data.resultObject.emailHint });
          }
        } catch (emailError) {
          console.error('Email verification error:', emailError);
          errors['onLogin'] = ["email_verification_error"];
        }
      } else if (result.data.error === "invalidConsents" || result.data.error === "oldConsents") {
        clearPassword = false;
        const consentsRoute = currentState.businessLogin ? "ConsentsAcceptanceB2b" : "ConsentsAcceptance";
        webTools.reloadToRoute(consentsRoute);
      } else if (result.data.error === RECAPTCHA_CHALLENGE_MAIL || result.data.error === RECAPTCHA_CHALLENGE_PHONE) {
        history.push({
          pathname: "/VerificationCodeLoginRecaptcha",
          search: window.location.search
        }, {
          codeSentTo: getCompleteUsername(currentState),
          previousPath: location.pathname,
          isBusinessFlow: currentState.businessLogin
        });
      } else if (result.data.response?.data === RECAPTCHA_ERROR) {
        errors['onLogin'] = [RECAPTCHA_ERROR];
      }

      if (result.data.error !== "invalidConsents" && result.data.error !== "oldConsents") {
        updateState({ showSpinner: false });
      }
    } else {
      pushAccountEvent(GaEventActionConstants.UserLoginSuccess);
      webTools.reloadToRoute();
    }

    if ((Object.keys(errors).length > 0 || error) && clearPassword) {
      model.password = "";
    }

    updateState({ errors, model, isProcessing: false });
    return error;
  }, [history, location, updateState, getCompleteUsername, getSelectedCountryPrefixValue, getSelectedCountryPrefixId, initialData]);

  const login = useCallback(async (recaptchaToken) => {    
    setState(currentState => {

      const { model, selectedPrefixId, isMobileApp, businessLogin } = currentState;
      let error = false;

      const validationResult = validator.validateLoginForm(model, selectedPrefixId, initialData.phonePrefixes);
      const errors = { 
        ...validationResult.errors,
        onLogin: ""
      };
      error = !validationResult.isValid;
      
      let selectedPhonePrefixValue = '';
      if (selectedPrefixId !== "") {
        selectedPhonePrefixValue = LegalEntityFunctions.getPhonePrefixFromId(selectedPrefixId, initialData.phonePrefixes);
      }

      if (!error) {
        // Perform async login operation
        const performLogin = async () => {
          try {
            const payload = {
              username: model.username,
              password: model.password,
              selectedCountry: localStorage.getItem(constants.SELECTED_COUNTRY),
              selectedPhonePrefix: selectedPhonePrefixValue,
              isMobileLogin: isMobileApp,
              businessLogin: businessLogin
            };

            const result = await AuthService.login(payload, recaptchaToken);
            await processLoginResult(result.response, false, currentState);
          } catch (loginError) {
            console.error('Login error:', loginError);
            setState(prevState => ({
              ...prevState,
              errors: { ...prevState.errors, onLogin: ["login_network_error"] },
              isProcessing: false,
              model: { ...prevState.model, password: "" }
            }));
          }
        };

        performLogin();
      } else {
        return {
          ...currentState,
          errors,
          model: { ...model, password: "" },
          isProcessing: false
        };
      }

      return {
        ...currentState,
        isProcessing: true
      };
    });
  }, [initialData, processLoginResult]);

  const handleRecaptchaTwoFactorFlow = useCallback(async () => {
    try {
      const result = await AuthService.loginAfterTwoFactor();
      if (!result.response) {
        updateState({ showSpinner: false });
        return;
      }

      setState(currentState => {
        processLoginResult(result.response, false, currentState);
        return currentState;
      });
    } catch (error) {
      console.error('Two factor flow error:', error);
      updateState({ showSpinner: false });
    }
  }, [updateState, processLoginResult]);

  const setInitialState = useCallback(() => {
    const isMobileApp = localStorage.getItem(constants.IS_MOBILE_APP_STORAGE_KEY) === constants.TRUE;
    const queriedSelectedCountry = urlTools.getQueryParamValue(location.search, constants.COUNTRY_URL_KEY);

    const isBusinessLoginValue = urlTools.getQueryParamValue(location.search, constants.BUSINESS_URL_KEY);
    let updates = { showSpinner: false, isMobileApp };

    if (isBusinessLoginValue) {
      updates.businessLogin = !!+isBusinessLoginValue;
      updates.isLoginTypeSwitchHidden = true;
    }

    if (queriedSelectedCountry) {
      const country = queriedSelectedCountry.toLowerCase();
      if (country === "lt" || country === "ee" || country === "lv") {
        localStorage.setItem(constants.SELECTED_COUNTRY, country);
      }
    }

    const countryLocal = localStorage.getItem(constants.SELECTED_COUNTRY);
    if (countryLocal !== null) {
      updates.selectedCountry = countryLocal;
    }

    updateState(updates);

    if (location.state?.verificationCodePassed) {
      updateState({ 
        showSpinner: true, 
        businessLogin: location.state.isBusinessFlow 
      });
      handleRecaptchaTwoFactorFlow();
    }
  }, [location, updateState, handleRecaptchaTwoFactorFlow]);

  const onDropDownChange = useCallback((e) => {
    const maxlength = validator.getPhoneMinLength(initialData.phonePrefixes, e.prefixValue);
    updateState({ selectedPrefixId: e.prefixId, maxPhoneLength: maxlength });
  }, [initialData.phonePrefixes, updateState]);


  const logout = useCallback(async () => {
    window.history.replaceState({}, '');
    AuthService.logout();
  }, []);

  const resetInitialState = useCallback(() => {
    console.debug("Resetting initial state in usePasswordLoginLogic");
    updateState({
      model: { username: "", password: "" },
      phoneError: false,
      errors: {},
      selectedCountry: null,
      clientId: "",
      legalEntity: "",
      showSpinner: true,
      selectedPrefixId: "",
      businessLogin: false,
      maxPhoneLength: "",
      showEmailSent: false,
      emailHint: "",
      email: "",
      emailError: "",
      showEnterEmail: false,
      isProcessing: false
    });
    setInitialState();
  }, [updateState, setInitialState]);

  return {
    state,
    updateState,
    onChange,
    switchLogin,
    login,
    executeUnauthenticatedRedirections,
    setInitialState,
    onDropDownChange,
    logout,
    resetInitialState,
    setIsProcessing,
  };
}